<script setup lang="ts">
/**
 * The one surface the portal shows for an address it cannot answer: a router path that
 * resolves to nothing, an entity that does not exist or was deleted, and the role refusal
 * that reads the same way to a customer. One panel, so a missing entity is never announced
 * twice in two different sets of words — and never under a page drawn as if it were there.
 */
import type { StudioViews } from '~/types/studio'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import ParamRow from '~/components/inspector/ParamRow.vue'

const props = withDefaults(defineProps<{
  kind?: 'missing' | 'forbidden'
  /** The address the reader asked for; the current route when nothing is passed. */
  address?: string
  /** Where the one way forward leads, and what it is called. */
  action?: string
  to?: string
}>(), { kind: 'missing', address: undefined, action: undefined, to: undefined })

const route = useRoute()
const session = useSession()
const { data: nav } = useStudioData<StudioViews['/api/org-nav']>('/api/org-nav')
const email = computed(() => session.value.data?.user.email ?? null)
const copy = computed(() => props.kind === 'forbidden'
  ? { title: 'Not allowed', code: 'http 403', icon: 'shield' as const, line: 'Your role in this organisation does not include this page. An owner or admin can change it.', action: 'Open my organisations', to: '/org/current' }
  : { title: 'Not found', code: 'http 404', icon: 'search' as const, line: 'The address does not exist, or the entity it pointed at was deleted.', action: 'Go to Overview', to: '/overview' })
</script>

<template>
  <section class="overflow-hidden rounded-lg border border-border bg-card">
    <header class="flex h-[var(--card-header-height)] items-center gap-2.5 border-b border-border px-3">
      <span class="flex items-center gap-1.5 text-xs font-semibold">
        <Icon :name="copy.icon" :size="12" />{{ copy.title }}
      </span>
      <span class="num text-[length:var(--type-meta)] text-muted-foreground">{{ copy.code }}</span>
    </header>

    <div class="flex flex-col gap-3 p-3.5">
      <p class="m-0 text-xs leading-relaxed text-pretty">{{ copy.line }}</p>
      <div class="flex flex-col gap-[var(--measure-7px)] rounded-md border border-border p-2.5">
        <ParamRow label="Address" :value="address ?? route.path" />
        <ParamRow v-if="email" label="Signed in as" :value="email" />
        <ParamRow v-if="nav?.org" label="Organisation" :value="nav.org" />
      </div>
      <div class="flex items-center gap-3">
        <ActionButton :label="action ?? copy.action" variant="gradient" size="lg" @click="navigateTo(to ?? copy.to)">
          <template #icon><Icon name="arrow" :size="13" /></template>
        </ActionButton>
        <NuxtLink to="/login" class="text-[length:var(--type-meta)] text-muted-foreground no-underline hover:text-foreground">Sign in as someone else</NuxtLink>
      </div>
    </div>
  </section>
</template>
